import dayjs from 'dayjs';
import utcPlugin from 'dayjs/plugin/utc';
import timezonePlugin from 'dayjs/plugin/timezone';
import { renderBreadcrumb } from './components/Breadcrumbs/buildBreadcrumb';
import { FilePdfOutlined, FileWordOutlined, FileExcelOutlined, FilePptOutlined, FileImageOutlined, PlayCircleOutlined, AudioOutlined, CalendarOutlined, FileTextOutlined, FileZipOutlined, LinkOutlined, FileOutlined, } from '@ant-design/icons';
import React from 'react';

// Needed by getDateWithoutTimeZone and getFormattedDateTimeRange below. Registered here
// rather than relying on another module having extended dayjs first.
dayjs.extend( utcPlugin );
dayjs.extend( timezonePlugin );

export const getFormattedDate = ( date ) => date && date !== null ? dayjs( date ).format( 'MMM D, YYYY' ) : '';
export const getFormattedDateTime = ( date ) => date && date !== null ? dayjs( date ).format( 'MMM D, YYYY h:mm A' ) : '';
export const removeTimeZone = ( dateTime ) => dateTime.replace( /(Z|[+-]\d{2}:?\d{2})$/, '' );
export const getDateWithoutTimeZone = ( dateTime, timezone ) => {
    return !dateTime || dateTime === null ?
        undefined
        :
        getFormattedDateTime( removeTimeZone( dayjs( new Date( dateTime ) ).tz( timezone ).format()  ) );
};

export const DATE_TIME_FORMAT = 'MMM D YYYY h:mm a';
const TIME_ONLY_FORMAT = 'h:mm a';
const PAYLOAD_FORMAT = 'YYYY-MM-DDTHH:mm:ssZ';
// A "floating" wall clock: no zone attached, just the date and time as displayed.
const FLOATING_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

/**
 * Helpers for forms that edit a datetime in an explicitly chosen timezone (ILT sessions,
 * leaderboards). Those forms hold *floating* values: local-mode dayjs objects whose wall
 * clock is the time as shown in the chosen zone. They are deliberately not zone-aware,
 * because antd/rc-picker builds new values with dayjs() / dayjs( text, format ) — always
 * local mode — as the user types or picks. Holding every value in one representation is
 * what keeps comparisons between two fields valid: mixing zone-aware and local-mode
 * values in one form silently breaks any isBefore/isAfter rule between them.
 *
 * Note that dayjs's two `tz` forms are different operations. `instance.tz( zone )` keeps
 * the instant and changes the displayed clock; `instance.tz( zone, true )` keeps the
 * displayed clock and changes the instant. Reading a picked time as belonging to the
 * chosen zone needs the second one.
 */

// floating wall clock in `timezone` -> the UTC instant the backend stores
export function formatDTPayload( dt, timezone ) {
    return dt?.tz( timezone, true ).utc().format( PAYLOAD_FORMAT ) ?? null;
}

// UTC instant from the backend -> floating wall clock in `timezone`, ready for a DatePicker
export function parseDTPayload( iso, timezone ) {
    return iso ? dayjs( dayjs.utc( iso ).tz( timezone ).format( FLOATING_FORMAT ) ) : null;
}

// "now" in the same floating space, so it can be compared against form values
export function nowInZone( timezone ) {
    return dayjs( dayjs().tz( timezone ).format( FLOATING_FORMAT ) );
}

// Re-reads a floating wall clock as belonging to `toTimezone` instead of `fromTimezone`,
// keeping the underlying instant. Always returns a new dayjs so antd sees a changed value.
export function shiftFloatingZone( dt, fromTimezone, toTimezone ) {
    if ( !dt || !fromTimezone || !toTimezone || fromTimezone === toTimezone ) {
        return dt ?? null;
    }
    return dayjs( dt.tz( fromTimezone, true ).tz( toTimezone ).format( FLOATING_FORMAT ) );
}

/**
 * Moves a form's floating datetime fields from one zone to another, keeping the instant
 * each one represents. Empty fields are left alone. Writing back new dayjs objects is
 * what makes the bound DatePickers repaint — mutating in place would not, since dayjs
 * is immutable and antd compares values by identity.
 *
 * @param {string} prevTz      IANA zone the fields' wall clocks currently mean
 * @param {string} tz          IANA zone to re-read them in
 * @param {string[]} fields    names of the datetime fields to shift
 * @param {object} form        the antd form instance holding them
 * @returns {void} the fields are updated on the form in place
 */
export function syncTimeFieldsToTimezone( prevTz, tz, fields, form ) {
    const timeValues = form.getFieldsValue( fields );
    const shifted = {};
    fields.forEach( f => {
        if ( timeValues[ f ] ) {
            shifted[ f ] = shiftFloatingZone( timeValues[ f ], prevTz, tz );
        }
    } );
    form.setFieldsValue( shifted );
}

// true when a form value still represents the instant already stored on the record
export function isUnchangedDT( storedISO, value, timezone ) {
    if ( !storedISO ) {
        return false;
    }
    return formatDTPayload( value, timezone ) === dayjs.utc( storedISO ).format( PAYLOAD_FORMAT );
}

// Parses anything dayjs accepts, optionally moving it into `timezone` for display.
// Returns null for missing or unparseable input so callers can fall back cleanly.
function toDisplayDate( value, timezone ) {
    if ( !value ) {
        return null;
    }

    const date = dayjs( value );

    if ( !date.isValid() ) {
        return null;
    }

    return timezone ? date.tz( timezone ) : date;
}

/**
 * Formats two datetimes as a range, dropping the repeated date when both land on the
 * same calendar day:
 *   different days -> 'Aug 11 2026 10:00 am - Aug 12 2026 2:00 pm'
 *   same day       -> 'Aug 11 2026 10:00 am - 2:00 pm'
 *
 * @param {*} start             range start — ISO string, Date, or dayjs
 * @param {*} end               range end — ISO string, Date, or dayjs
 * @param {string} [timezone]   IANA zone to display in (e.g. a session's timezone).
 *                              Omit to use the browser's zone. The same-day comparison
 *                              is made in this zone, so a range can read as one day in
 *                              one zone and two in another.
 * @returns {string} the formatted range; a lone datetime if only one side is given, or
 *                   '' if neither is.
 */
export function getFormattedDateTimeRange( start, end, timezone ) {
    const from = toDisplayDate( start, timezone );
    const to = toDisplayDate( end, timezone );

    if ( !from && !to ) {
        return '';
    }

    if ( !from || !to ) {
        return ( from ?? to ).format( DATE_TIME_FORMAT );
    }

    const isSameDay = from.format( 'YYYY-MM-DD' ) === to.format( 'YYYY-MM-DD' );

    return `${ from.format( DATE_TIME_FORMAT ) } - ${ to.format( isSameDay ? TIME_ONLY_FORMAT : DATE_TIME_FORMAT ) }`;
}

export const getEnvironment = () => {
    const host = window.location.hostname.toLowerCase();

    if ( host.includes( 'localhost' ) || host.includes( 'qa.' ) ) {
        return 'qa';
    }
    
    if ( host.includes( 'pprd.' ) ) {
        return 'pprd';
    }
    
    return 'prod';
};

export const getErrorMessages = ( response ) => {
    const errorBody = response.response?.graphQLErrors?.flatMap( g => Object.values( g?.extensions?.response?.body?.errors?.fields ) ?? [] )?.join( '\n' );
    return errorBody || 'An unknown error occurred';
};

export const validateEmail = ( email ) => {
    return String( email )
        .toLowerCase()
        .match(
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        );
};

export const validateUUID = ( uuid ) => {
    return !!uuid?.match( /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i );
};

/******************************************
 * Functions for loading federated modules
 ******************************************/

export const loadScope = ( url, scope ) => {
    const element = document.createElement( 'script' );
    const promise = new Promise( ( resolve, reject ) => {
        element.src = url;
        element.type = 'text/javascript';
        element.async = true;
        element.onload = () => resolve( window[ scope ] );
        element.onerror = reject;
    } );
    document.head.appendChild( element );
    promise.finally( () => document.head.removeChild( element ) );
    return promise;
};

export const loadModule = async ( url, scope, module ) => {
    try {
        const container = await loadScope( url, scope );
        await __webpack_init_sharing__( 'default' );// eslint-disable-line
        await container.init( __webpack_share_scopes__.default );// eslint-disable-line
        const factory = await container.get( module );
        return factory();
    } catch ( error ) {
        console.error( 'Error loading module:', error );
    }
};


/******************************************
 * Misc functions from legacy code
 ******************************************/

export const isRelativePath = ( path ) => {
    return !( /^https?:\/\//i ).test( path );
};

export const isJsonString = ( value ) => {
    const isString = ( typeof value === 'string' );
    if ( isString ) {
        const trimmedStr = value.trim();
        if ( trimmedStr && trimmedStr.length > 0 ) {
            return trimmedStr[ 0 ] === '{';
        }
    }

    return false;
};

export const download = ( dataurl, filename ) => {
    const link = document.createElement( 'a' );
    link.href = dataurl;
    link.download = filename;
    link.click();
};

export const buildPageHeaderBreadcrumb = ( routes = [] ) => {
    // Normalise the legacy `{ breadcrumbName, path }` shape to the shared
    // renderer's `{ name, path }` shape, then render the SAME antd <Breadcrumb>
    // element the <Breadcrumbs> component produces.
    const crumbs = routes.map( ( route ) => ( {
        name: route?.breadcrumbName ?? route?.title,
        path: route?.path,
    } ) );

    return renderBreadcrumb( crumbs );
};

export const reportError = async ( message ) => {
    if ( window.location.host.startsWith( 'localhost' ) ) {
        console.warn( message );
        return;
    }

    window.location.href = [
        'mailto:team-hoya-errors-aaaaif6eyjtqbwkmbcpa2yk6ee@biworldwide.slack.com',
        `?subject=${ encodeURI( 'User Reported Course Studio Error in ' + getEnvironment().toUpperCase() ) }`,
        `&body=${ encodeURI( message ) }`
    ].join();
};

export const isValidURL = ( url ) => {
    try {
        new URL( url );
        return true;
        
    // eslint-disable-next-line no-unused-vars
    } catch ( e ) {
        return false;
    }
};

export const getFileTypeSpecs = ( file ) => {

    // colors are the shade-7 tone of an antd preset hue - the same shade a preset Tag uses for its text

    const FILE_TYPE_SPECS = {
        PDF: {
            key: 'PDF',
            name: 'PDF',
            color: '#cf1322',
            icon: <FilePdfOutlined/>,
            fileCheck: ( { mimetype } ) => [ 'application/pdf' ].includes( mimetype )
        },
        WORD: {
            key: 'WORD',
            name: 'Word',
            color: '#1d39c4',
            icon: <FileWordOutlined/>,
            fileCheck: ( { mimetype } ) => [ 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ].includes( mimetype )
        },
        EXCEL: {
            key: 'EXCEL',
            name: 'Excel',
            color: '#389e0d',
            icon: <FileExcelOutlined/>,
            fileCheck: ( { mimetype } ) => [ 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ].includes( mimetype )
        },
        PPT: {
            key: 'PPT',
            name: 'Powerpoint',
            color: '#d4380d',
            icon: <FilePptOutlined/>,
            fileCheck: ( { mimetype } ) => [ 'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ].includes( mimetype )
        },
        IMAGE: {
            key: 'IMAGE',
            name: 'Image',
            color: '#531dab',
            icon: <FileImageOutlined/>,
            fileCheck: ( { mimetype } ) => mimetype?.startsWith( 'image/' )
        },
        VIDEO: {
            key: 'VIDEO',
            name: 'Video',
            color: '#c41d7f',
            icon: <PlayCircleOutlined/>,
            fileCheck: ( { mimetype } ) => mimetype?.startsWith( 'video/' )
        },
        AUDIO: {
            key: 'AUDIO',
            name: 'Audio',
            color: '#08979c',
            icon: <AudioOutlined/>,
            fileCheck: ( { mimetype } ) => mimetype?.startsWith( 'audio/' )
        },
        CALENDAR: {
            key: 'CALENDAR',
            name: 'Calendar',
            color: '#d48806',
            icon: <CalendarOutlined />,
            fileCheck: ( { mimetype } ) => mimetype == 'text/calendar'
        },
        TEXT: {
            key: 'TEXT',
            name: 'Text',
            color: '#7cb305',
            icon: <FileTextOutlined />,
            fileCheck: ( { mimetype } ) => mimetype?.startsWith( 'text/' )
        },
        ZIP: {
            key: 'ZIP',
            name: 'ZIP',
            color: '#d46b08',
            icon: <FileZipOutlined />,
            fileCheck: ( { mimetype } ) => mimetype == 'application/zip'
        },
        URL: {
            key: 'URL',
            name: 'URL',
            color: '#0958d9',
            icon: <LinkOutlined/>,
            fileCheck: ( { url } ) => !!url
        },
        //application/zip
        _: {
            key: 'OTHER',
            name: 'Unknown',
            color: '#8c8c8c',
            icon: <FileOutlined/>,
            fileCheck: () => true
        }
    };

    if ( !file ) {
        return FILE_TYPE_SPECS._;
    }

    return Object.values( FILE_TYPE_SPECS ).find( f => f.fileCheck( file ) );
};
